import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import { Submission } from '../../models/submission.model';
import { SubmissionService } from '../../submission-data/submission.service';
import { taxYears as stubTaxYears } from '../../submission-data/dataStub';
import { GlobalService } from '../../../global/services/global.service';
import {
  EfileGridCallbackEvent,
  EfileGridConfig,
  EfileGridDirective,
} from '../../shared/efile-grid.directive';

/**
 * Angular 12 port of legacy ui-router state:
 *   `submissions-dasboard.submission.overrides`
 *   (ui/app/components/efile/efile.js L163; grid_id=5886, action_id=32546)
 *
 * Legacy `overridesController` (`EfileElementsController.js` L3393+):
 *   - Loads grid via `GridFactory.getDataCustom` with params:
 *       `tax_year`, `type` (submission type, e.g. federal), `submission_id`
 *   - Toolbar: refresh icon, tax year `<select>` (disabled; mirrors submission),
 *     and Filter button — data loads on those actions only (`autoLoad: false`),
 *     not on tab mount (matches submission list “click Filter to load” pattern).
 */
@Component({
    selector: 'gtw-efile-overrides',
    templateUrl: './efile-overrides.component.html',
    styleUrls: ['./efile-overrides.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EfileOverridesComponent implements OnInit, OnChanges {
  gridConfig: EfileGridConfig | null = null;

  @ViewChild('overridesGrid') gridRef?: EfileGridDirective;

  /**
   * Active submission row from `submission-detail` (preferred). Falls back to
   * `SubmissionService.activeSubmission` when the host omits the input.
   */
  @Input() submission: Submission | null = null;

  /** List-level tax year from submission shell (optional fallback). */
  @Input() taxYear: any;

  /** Federal / state — same semantics as legacy `vm.submissionType`. */
  @Input() submissionType: string = 'federal';

  /** Options for the tax year dropdown (legacy: `GlobalService.inputs.tax_year.values`). */
  taxYearOptions: Array<{ value: any; label: string; [k: string]: any }> = [];

  showOverrideModal = false;
  overrideRowData: any = null;
  selectedSubmission: any = null;

  constructor(
    private cdr: ChangeDetectorRef,
    private submissionService: SubmissionService,
    private globalService: GlobalService,
  ) {}

  ngOnInit(): void {
    this.hydrateTaxYearOptions();
    this.applyGridFromInputs();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['submission'] ||
      changes['taxYear'] ||
      changes['submissionType']
    ) {
      this.hydrateTaxYearOptions();
      this.applyGridFromInputs();
    }
  }

  hasActiveSubmission(): boolean {
    return !!this.resolveSubmission()?.SUBMISSION_ID;
  }

  /** Tax year bound to the toolbar `<select>` (legacy: submission's year, disabled). */
  toolbarTaxYearModel(): any {
    const sub = this.resolveSubmission();
    if (sub?.TAX_YEAR != null) {
      return sub.TAX_YEAR;
    }
    return this.taxYear ?? null;
  }

  onToolbarRefresh(): void {
    this.gridRef?.refresh();
  }

  onToolbarFilter(): void {
    this.gridRef?.refresh();
  }

  /**
   * gizmoGrid callback handler. Legacy `ctrlFn(callName, data, type)`
   * opens the override modal regardless of the callName; we mirror that
   * but skip gizmo's internal `columnTemplate` events.
   */
  onGridCallback(event: EfileGridCallbackEvent): void {
    if (!event || event.type === 'columnTemplate') return;
    const row = event.data?.rowData ?? event.data;
    if (!row || typeof row !== 'object') return;

    this.overrideRowData = row;
    this.selectedSubmission = this.resolveSubmission() || this.submissionService.activeSubmission || null;
    this.showOverrideModal = true;
    this.cdr.markForCheck();
  }

  onOverrideUpdate(detail: any): void {
    this.showOverrideModal = false;
    this.overrideRowData = null;
    this.cdr.markForCheck();

    if (detail === 'refresh' && this.gridRef) {
      this.gridRef.refresh();
    }
  }

  isSelectedTaxYearOption(opt: { value: any; label: string }): boolean {
    const cur = this.toolbarTaxYearModel();
    return String(opt?.value ?? '') === String(cur ?? '');
  }

  private resolveSubmission(): Submission | null {
    if (this.submission && (this.submission as any).SUBMISSION_ID != null) {
      return this.submission;
    }
    const svc = this.submissionService.activeSubmission as Submission | undefined;
    return svc ?? null;
  }

  private hydrateTaxYearOptions(): void {
    const input = this.globalService.global?.inputs?.tax_year as any;
    const values = input?.values;
    if (Array.isArray(values) && values.length > 0) {
      this.taxYearOptions = values;
    } else {
      this.taxYearOptions = stubTaxYears;
    }
  }

  private effectiveTaxYear(): string | number {
    const sub = this.resolveSubmission();
    if (sub?.TAX_YEAR != null) {
      return sub.TAX_YEAR;
    }
    if (this.taxYear != null && this.taxYear !== '') {
      return this.taxYear;
    }
    return '';
  }

  private applyGridFromInputs(): void {
    const sub = this.resolveSubmission();
    const sid = sub?.SUBMISSION_ID;
    if (sid == null) {
      this.gridConfig = null;
      this.cdr.markForCheck();
      return;
    }

    const ty = this.effectiveTaxYear();
    const typ = String(this.submissionType || 'federal')
      .trim()
      .toLowerCase();

    this.gridConfig = {
      actionId: 32546,
      gridId: '5886',
      autoLoad: false,
      extraParams: {
        submission_id: sid,
        tax_year: ty,
        type: typ,
      },
      cacheKey: `efile/overrides/${sid}/${ty}/${typ}`,
    };
    this.cdr.markForCheck();
  }
}
